/** Media API bindings (M9.5). Feature code imports only from here. */
import { http } from "@/shared/api/http";

export type MediaKind =
  | "image" | "video" | "voice" | "music" | "reel" | "thumbnail_set" | "caption";

export type RecipeStatus =
  | "draft" | "ready" | "rendering" | "succeeded" | "failed" | "rejected";

export type AssetStatus = "rendering" | "ready" | "failed" | "rejected";

export type RouterPolicy =
  | "cheapest_brand_locked" | "cheapest" | "fastest" | "highest_quality" | "balanced";

export interface RenderReadiness {
  brand_lock_pct: number;
  visual_assets_pct: number;
  cta_pct: number;
  voice_pct: number;
  music_pct: number;
  overall_pct: number;
  missing: string[];
}

export interface RenderTelemetry {
  rendering_time_ms: number;
  cost_usd: number;
  quality_score: number;
  brand_consistency_score: number;
  prompt_completeness_score: number;
  visual_balance_score: number;
  readability_score: number;
  platform_optimization_score: number;
  cta_clarity_score: number;
}

export interface CostEstimate {
  per_capability_usd: Record<string, number>;
  total_usd: number;
  currency: string;
}

export interface BrandSnapshot {
  workspace_id: string;
  palette: string[];
  logo_url: string | null;
  fonts: string[];
  voice_traits: string[];
  tone: string;
  cta: string;
  audience: string;
  industry: string;
  visual_style: string;
}

export interface Scene {
  seq: number;
  duration_ms: number;
  shot: { camera: string; lens: string; movement: string; angle: string };
  dialogue: string;
  voice_key: string;
  captions: string;
  transition: string;
  music_cue: string;
  cta: string | null;
  b_roll_notes: string;
}

export interface RouterDecision {
  provider_key: string;
  model: string;
  reason: string;
  picked_from: string[];
  policy: string;
}

export interface MediaRecipe {
  id?: string;
  _id?: string;
  workspace_id: string;
  kind: MediaKind;
  intent: string;
  channel: string | null;
  status: RecipeStatus;
  scenes: Scene[];
  brand_snapshot: BrandSnapshot | null;
  cost_estimate: CostEstimate;
  readiness: RenderReadiness;
  telemetry: RenderTelemetry | null;
  router_decision: RouterDecision | null;
  aspect_ratio: string;
  duration_ms: number;
  parent_asset_id: string | null;
  recipe_version: number;
  child_recipe_ids: string[];
  created_at?: string;
}

export interface AssetRef {
  url: string;
  storage_key: string;
  size_bytes: number;
  mime: string;
  score?: number;
  palette?: string[];
}

export interface MediaAsset {
  id?: string;
  _id?: string;
  workspace_id: string;
  recipe_id: string;
  kind: MediaKind;
  status: AssetStatus;
  primary_asset_ref: AssetRef | null;
  preview_asset_ref: AssetRef | null;
  poster_asset_ref: AssetRef | null;
  storyboard_pdf_ref: AssetRef | null;
  shot_list_pdf_ref: AssetRef | null;
  channel_asset_refs: Record<string, AssetRef>;
  telemetry: RenderTelemetry;
  reject_reason: string | null;
  created_at?: string;
}

export interface ReelResult {
  reel_recipe_id: string;
  intent: string;
  channels: string[];
  cost_estimate_total_usd: number;
  child_assets: Array<{
    channel: string;
    recipe_id: string;
    asset_id: string;
    status: string;
    quality_score: number;
    primary_url: string | null;
  }>;
}

export interface MemoryEntry {
  _id: string;
  workspace_id: string;
  kind: string;
  value: Record<string, unknown>;
  score: number;
  sample_size: number;
  source: string;
}

export interface ProvidersMap {
  [kind: string]: Array<{ key: string; cost_per_call_usd: number; quality_score: number; class: string }>;
}

const base = (ws: string) => `/workspaces/${ws}/media`;

export const listProviders = (): Promise<ProvidersMap> => http(`/media/providers`);

export const createRecipe = (
  ws: string, body: { kind: MediaKind; intent: string; channel?: string | null; campaign_pack_id?: string | null },
): Promise<MediaRecipe> => http(`${base(ws)}/recipes`, { method: "POST", body });

export const renderRecipe = (
  ws: string, recipeId: string, policy: RouterPolicy = "cheapest_brand_locked",
): Promise<MediaAsset> =>
  http(`${base(ws)}/recipes/${recipeId}/render`, { method: "POST", body: { policy } });

export const rerenderRecipe = (
  ws: string, recipeId: string, policy: RouterPolicy,
): Promise<MediaAsset> =>
  http(`${base(ws)}/recipes/${recipeId}/rerender`, { method: "POST", body: { policy } });

export const generateReel = (
  ws: string, body: { intent: string; channels?: string[] | null; campaign_pack_id?: string | null },
): Promise<ReelResult> => http(`${base(ws)}/reels/generate`, { method: "POST", body });

export const listRecipes = (ws: string): Promise<MediaRecipe[]> => http(`${base(ws)}/recipes`);
export const getRecipe = (ws: string, id: string): Promise<MediaRecipe> => http(`${base(ws)}/recipes/${id}`);
export const listAssets = (ws: string): Promise<MediaAsset[]> => http(`${base(ws)}/assets`);
export const listMemory = (ws: string): Promise<MemoryEntry[]> => http(`${base(ws)}/memory`);
export const listRouterDecisions = (ws: string): Promise<Array<{
  _id: string; kind: string; channel: string | null; intent: string;
  router_decision: RouterDecision | null; telemetry: RenderTelemetry | null; created_at?: string;
}>> => http(`${base(ws)}/router/decisions`);

/* ── Diagnostics visibility (owner/admin only) ─────────────────── */

export interface DiagnosticsState {
  workspace_id: string;
  role: string;
  diagnostics_enabled: boolean;
  can_toggle: boolean;
}

export const getDiagnosticsState = (ws: string): Promise<DiagnosticsState> =>
  http(`${base(ws)}/diagnostics/state?workspace_id=${ws}`);

export const setDiagnosticsEnabled = (ws: string, enabled: boolean): Promise<DiagnosticsState> =>
  http(`${base(ws)}/diagnostics/toggle?workspace_id=${ws}`, { method: "POST", body: { enabled } });

/* ── Preview/demo asset seeder (owner-only) ────────────────────── */

export interface SeedAssetsResult {
  workspace_id: string;
  seeded: number;
  inserted: number;
  updated: number;
  total_assets: number;
}

export const seedSampleAssets = (ws: string): Promise<SeedAssetsResult> =>
  http(`${base(ws)}/dev-seed`, { method: "POST" });

/* ── Tone tokens shared with M9 intelligence look-and-feel ─────── */
export const READINESS_TONES = (pct: number): string => {
  if (pct >= 80) return "text-emerald-500";
  if (pct >= 60) return "text-amber-500";
  return "text-rose-500";
};

export const STATUS_TONES: Record<string, string> = {
  ready: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  succeeded: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  rendering: "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200",
  draft: "bg-zinc-100 text-zinc-900 dark:bg-zinc-950/60 dark:text-zinc-300",
  failed: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
  rejected: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
};

export const DEFAULT_REEL_CHANNELS = [
  "instagram_reel", "facebook_reel", "tiktok", "youtube_short", "pinterest_idea_pin",
];
